import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Moon, Sparkles, Flame, Loader2, Volume2, BookOpen, Check } from "lucide-react";
import { fetchLunar, fetchLunarRitual, addJournal } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const days = (d) => {
  const n = Math.max(0, Math.round(d));
  if (n === 0) return "today";
  return `${n} day${n === 1 ? "" : "s"}`;
};

const MoonMarker = ({ label, event, highlight }) => (
  <div
    className={`flex items-center gap-3 rounded-2xl border px-5 py-4 ${
      highlight ? "border-[#C5A059]/50 bg-[#C5A059]/5" : "border-[#C5A059]/12 bg-black/30"
    }`}
  >
    <Moon size={18} className={highlight ? "text-[#C5A059]" : "text-white/40"} />
    <div>
      <p className="overline text-[0.5rem]">{label}</p>
      <p className="mt-0.5 text-sm font-light text-white/80">
        {event.glyph} {event.sign} · in {days(event.days_until)}
      </p>
    </div>
  </div>
);

export const LunarPrompt = ({ onComplete }) => {
  const [lunar, setLunar] = useState(null);
  const [ritual, setRitual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetchLunar().then(setLunar).catch(() => {});
  }, []);

  if (!lunar) return null;

  const active = lunar.active;
  const soon =
    lunar.next_new.days_until <= lunar.next_full.days_until ? lunar.next_new : lunar.next_full;
  const target = active || soon;
  const isNew = target.type === "new";
  const label = isNew ? "New Moon" : "Full Moon";

  const enter = async () => {
    if (ritual) return setOpen((o) => !o);
    setLoading(true);
    try {
      const r = await fetchLunarRitual(false);
      setRitual(r);
      setOpen(true);
    } catch (e) {
      toast.error("The Moon is veiled. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  const completeRitual = async () => {
    if (!ritual) return;
    setSaving(true);
    try {
      await addJournal({
        event_type: ritual.event.type,
        event_date: ritual.event.date.slice(0, 10),
        sign: ritual.event.sign,
        title: ritual.title,
        reflection: reflection.trim(),
      });
      setDone(true);
      toast.success("The ritual is kept in your lunar diary.");
      onComplete && onComplete();
    } catch (e) {
      toast.error("The diary could not receive it. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease }}
      className={`mt-8 overflow-hidden rounded-3xl border ${
        active
          ? "border-[#C5A059]/45 bg-gradient-to-br from-[#141020] to-[#080610] shadow-[0_0_60px_rgba(197,160,89,0.12)]"
          : "border-[#C5A059]/15 bg-[#070707]"
      }`}
      data-testid="lunar-prompt"
    >
      <div className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="overline text-[0.6rem]">
            {active ? "The ritual window is open" : "The turning of the Moon"}
          </p>
          <h3 className="mt-3 font-serif text-[clamp(1.4rem,3vw,2.2rem)] font-light italic leading-tight text-white">
            {active ? (
              <>
                The <span className="text-[#C5A059]">{label}</span> gathers in {target.glyph}{" "}
                {target.sign}
                {Math.round(target.days_until) === 0 ? " — tonight." : "."}
              </>
            ) : (
              <>
                Your next <span className="text-[#C5A059]">{label}</span> ritual awaits in{" "}
                {days(target.days_until)}.
              </>
            )}
          </h3>
          <div className="mt-6 flex flex-wrap gap-3">
            <MoonMarker label="Next New Moon" event={lunar.next_new} highlight={target.type === "new"} />
            <MoonMarker label="Next Full Moon" event={lunar.next_full} highlight={target.type === "full"} />
          </div>
        </div>
        <button
          onClick={enter}
          disabled={loading}
          className={`group inline-flex shrink-0 items-center justify-center gap-3 rounded-full px-8 py-4 text-xs font-medium uppercase tracking-[0.22em] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 ${
            active
              ? "border border-[#C5A059] bg-[#C5A059] text-[#050505]"
              : "border border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#050505]"
          }`}
          data-testid="lunar-ritual-btn"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading
            ? "Drawing the tide…"
            : ritual
            ? open
              ? "Hide ritual"
              : "Show ritual"
            : active
            ? "Begin the ritual"
            : "Preview the ritual"}
        </button>
      </div>

      <AnimatePresence>
        {ritual && open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease }}
            className="overflow-hidden border-t border-[#C5A059]/15"
            data-testid="lunar-ritual-result"
          >
            <div className="p-8 md:p-10">
              <p className="overline text-[0.55rem]">
                {label} in {ritual.event.glyph} {ritual.event.sign}
              </p>
              <h4 className="mt-3 font-serif text-3xl font-light text-[#C5A059]">{ritual.title}</h4>
              <p className="mt-4 max-w-2xl text-base font-light leading-relaxed text-white/70">
                {ritual.invocation}
              </p>

              <div className="mt-7 grid grid-cols-1 gap-3 md:grid-cols-3">
                {(ritual.intentions || []).map((t, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-[#C5A059]/15 bg-black/40 p-5 text-sm font-light leading-relaxed text-white/65"
                  >
                    <span className="mb-2 block font-serif text-lg text-[#C5A059]">0{i + 1}</span>
                    {t}
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#5a4a1f]/40 bg-[#C5A059]/5 px-5 py-4">
                <Flame size={18} className="mt-0.5 shrink-0 text-[#C5A059]" />
                <p className="text-sm font-light italic text-[#e8d3a6]">{ritual.offering}</p>
              </div>

              <div className="mt-5 flex items-start gap-3">
                <Volume2 size={16} className="mt-1 shrink-0 text-white/40" />
                <p className="font-serif text-xl font-light italic leading-relaxed text-white/80">
                  “{ritual.affirmation}”
                </p>
              </div>

              <div className="mt-8 border-t border-[#C5A059]/15 pt-6" data-testid="ritual-complete">
                <p className="overline mb-3 text-[0.55rem]">Seal this ritual in your diary</p>
                <textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  rows={2}
                  placeholder="A word on what you planted or released…"
                  className="w-full resize-none rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                  data-testid="ritual-reflection-input"
                />
                <button
                  onClick={completeRitual}
                  disabled={saving || done}
                  className="group mt-3 inline-flex items-center gap-2 rounded-full border border-[#C5A059] px-7 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-60"
                  data-testid="ritual-complete-btn"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : done ? (
                    <Check size={14} />
                  ) : (
                    <BookOpen size={14} />
                  )}
                  {saving ? "Sealing…" : done ? "Kept in your diary" : "Complete & keep this ritual"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default LunarPrompt;
